const normalizeValue = value => String(value ?? '').trim();

const compareText = (left, right) => left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: 'base'
});

export const getSalesActivityOverviewTotal = rows => rows.reduce((total, row) => total + Number(row.Count || 0), 0);

export const buildReportSubreportData = (reportData = [], settings = {}) => {
    if (settings.type !== 'latestStatusMilestones') return [];

    const identityAccessor = settings.identityAccessor;
    const fallbackIdentityAccessor = settings.fallbackIdentityAccessor;
    const statusAccessor = settings.statusAccessor;
    const groupAccessor = settings.groupAccessor;
    const dateAccessor = settings.dateAccessor;
    const companyAccessor = settings.companyAccessor;
    const groupColumn = settings.groupColumn || groupAccessor;
    const statusColumn = settings.statusColumn || 'Current Status';
    const countColumn = settings.countColumn || 'Count';
    const companiesColumn = settings.companiesColumn || 'Companies';
    const configuredMilestones = (settings.milestones || [])
        .map(milestone => typeof milestone === 'string'
            ? { value: milestone, label: milestone }
            : { value: normalizeValue(milestone.value), label: normalizeValue(milestone.label || milestone.value) })
        .filter(milestone => milestone.value);
    const availableStatuses = (settings.availableValues || [])
        .map(status => typeof status === 'string'
            ? { value: status, label: status }
            : { value: normalizeValue(status.value), label: normalizeValue(status.label || status.value) })
        .filter(status => status.value);
    const milestoneValues = new Set(configuredMilestones.map(milestone => milestone.value));
    const nonCumulativeStatuses = new Set((settings.nonCumulativeStatuses || []).map(normalizeValue));
    const latestByIdentity = new Map();

    reportData.forEach((row, index) => {
        const identity = normalizeValue(row?.[identityAccessor])
            || normalizeValue(row?.[fallbackIdentityAccessor]);
        const status = normalizeValue(row?.[statusAccessor]);
        const group = normalizeValue(row?.[groupAccessor]);
        const company = normalizeValue(row?.[companyAccessor]) || identity;
        if (!identity || !status) return;

        const parsedDate = new Date(row?.[dateAccessor]).getTime();
        const timestamp = Number.isNaN(parsedDate) ? 0 : parsedDate;
        const identityKey = groupColumn ? `${group}\u0001${identity}` : identity;
        const current = latestByIdentity.get(identityKey);
        if (!current || timestamp > current.timestamp || (timestamp === current.timestamp && index < current.index)) {
            latestByIdentity.set(identityKey, { status, group, company, timestamp, index });
        }
    });

    const makeTotal = (group, status) => ({
        group,
        label: status.label,
        count: 0,
        companies: new Map()
    });
    const statusLabels = new Map(availableStatuses.map(status => [status.value, status.label]));
    configuredMilestones.forEach(milestone => statusLabels.set(milestone.value, milestone.label));
    const totals = new Map();

    const getTotal = (group, status) => {
        const keyParts = [];
        if (groupColumn) keyParts.push(group);
        keyParts.push(status);
        const key = keyParts.join('\u0001');

        if (!totals.has(key)) {
            totals.set(key, makeTotal(group, {
                value: status,
                label: statusLabels.get(status) || status
            }));
        }

        return totals.get(key);
    };

    if (!groupColumn) {
        availableStatuses.forEach(status => getTotal('', status.value));
        configuredMilestones.forEach(milestone => getTotal('', milestone.value));
    }

    const addCompany = (total, identity, company) => {
        total.count += 1;
        total.companies.set(identity, company);
    };

    latestByIdentity.forEach(({ status, group, company }, identity) => {
        if (settings.cumulative && !nonCumulativeStatuses.has(status)) {
            configuredMilestones.forEach(milestone => {
                if (compareText(milestone.value, status) <= 0) {
                    addCompany(getTotal(group, milestone.value), identity, company);
                }
            });
        }

        if (statusLabels.has(status)) {
            if (!settings.cumulative || !milestoneValues.has(status)) addCompany(getTotal(group, status), identity, company);
        } else if (settings.includeLatestStatus !== false) {
            addCompany(getTotal(group, status), identity, company);
        }
    });

    return [...totals.values()]
        .sort((left, right) => {
            if (groupColumn) {
                const groupSort = compareText(left.group, right.group);
                if (groupSort !== 0) return groupSort;
            }

            return compareText(left.label, right.label);
        })
        .map(total => ({
            ...(groupColumn ? { [groupColumn]: total.group } : {}),
            [statusColumn]: total.label,
            [countColumn]: total.count,
            [companiesColumn]: [...total.companies.values()].sort(compareText).join(', ')
        }))
        .filter(total => !settings.hideZeroTotals || total[countColumn] > 0);
};
export const buildStatusPivot = (listingRows = [], availableValues = [], settings = {}) => {
    const groupColumn = settings.groupColumn || 'Current Type';
    const statusColumn = settings.statusColumn || 'Current Status';
    const countColumn = settings.countColumn || 'Count';
    const statuses = [...new Set([
        ...availableValues.map(value => normalizeValue(typeof value === 'string' ? value : value.label || value.value)),
        ...listingRows.map(row => normalizeValue(row[statusColumn]))
    ].filter(Boolean))].sort(compareText);
    const groups = new Map();
    listingRows.forEach(row => {
        const group = normalizeValue(row[groupColumn]);
        if (!groups.has(group)) groups.set(group, { [groupColumn]: group });
        groups.get(group)[normalizeValue(row[statusColumn])] = row[countColumn];
    });
    const rows = [...groups.values()].sort((left, right) => compareText(left[groupColumn], right[groupColumn]));
    const visibleStatuses = statuses.filter(status => rows.some(row => (row[status] ?? 0) > 0));
    const data = rows.map(row => Object.fromEntries([groupColumn, ...visibleStatuses].map(name => [name, row[name] ?? 0])));
    data.push({
        [groupColumn]: 'TOTALS',
        ...Object.fromEntries(visibleStatuses.map(status => [status, data.reduce((sum, row) => sum + Number(row[status] || 0), 0)])),
        isTotalsRow: true
    });
    return {
        columns: [groupColumn, ...visibleStatuses].map((name, index) => ({
            Header: name,
            accessor: name,
            width: index === 0 ? 220 : 150,
            renderCell: index === 0 ? 'text' : 'zero_as_dash'
        })),
        data
    };
};
